import { z } from 'zod';
import { isoDateTimeSchema, uuidSchema } from './common.js';
/**
 * Tag category — closed dictionary entry (admin-arch §2.4, PRD §5.2 +
 * decisions §45.11 for antishazam). `is_hidden` semantics per backend F-15:
 * hidden categories survive on already-tagged tracks but are excluded from
 * pickers / filters. `is_hidden` is NOT a soft-delete.
 */
/**
 * WHERE a category may be used (venue-category-discovery arch §2.2, migration
 * 0116). «Часть дня» is meaningless on a track, and `is_hidden` is deliberately
 * NOT the lever — it means «retired, read-only», and a live dictionary must not
 * look retired.
 */
export const tagCategoryScopeSchema = z.enum(['track', 'preset', 'both']);
/**
 * «Годится для этой поверхности» — a SUPERSET test, not an equality one:
 * `track` admits `scope IN ('track','both')`. Deliberately not named `scope`,
 * because `scope=track` reads like equality and would silently drop the `both`
 * categories — which are `mood` / `energy` / `genre`, i.e. almost the whole
 * track picker.
 */
export const tagUsableForSchema = z.enum(['track', 'preset', 'any']);
export const tagCategorySchema = z.object({
    id: uuidSchema,
    code: z.string().min(1).max(64),
    name: z.string().min(1).max(255),
    is_system: z.boolean(),
    is_hidden: z.boolean(),
    sort_order: z.number().int().nullable(),
    /**
     * Present since venue-category-discovery. Without it on the wire the FE
     * cannot tell a preset-only category from a track one, which is how the
     * `day_part` tags reached the track tag picker — a control that writes
     * «Утро» into `content.track_tags`.
     */
    scope: tagCategoryScopeSchema,
    /**
     * The group heading a venue reads in the track search filters (STORY-100,
     * migration 0137); `null` keeps the category out of them. `name` stays the
     * admin label. Optional like a tag's `client_name`: the field is additive.
     */
    client_name: z.string().min(1).max(255).nullable().optional(),
});
export const tagCategoryCreateSchema = z
    .object({
    code: z
        .string()
        .min(1)
        .max(64)
        .regex(/^[a-z][a-z0-9_]*$/, 'Lowercase snake_case'),
    name: z.string().min(1).max(255),
    sort_order: z.number().int().optional(),
    /**
     * Which surfaces the category's tags describe. Writable since CEO
     * 2026-08-31 — it decides whether the category becomes a filter for
     * VENUES, and until now it was stored but settable by nobody, so adding a
     * client filter meant a hand-written UPDATE. Defaults to `track`: a new
     * category is a curator's tool until someone says otherwise.
     */
    scope: tagCategoryScopeSchema.optional(),
    /** See `tagCategorySchema.client_name`; the form sends `null` for an empty field. */
    client_name: z.string().trim().min(1).max(255).nullable().optional(),
})
    .strict();
export const tagCategoryUpdateSchema = z
    .object({
    name: z.string().min(1).max(255).optional(),
    /** `null` снимает свой порядок и возвращает категорию в алфавитный хвост. */
    sort_order: z.number().int().nullable().optional(),
    is_hidden: z.boolean().optional(),
    /** See `tagCategoryCreateSchema.scope`. Widening a category to `preset` or
     *  `both` is what puts its tags in front of venues. */
    scope: tagCategoryScopeSchema.optional(),
    /** `null` clears it, and the category leaves the track filters. */
    client_name: z.string().trim().min(1).max(255).nullable().optional(),
})
    .strict()
    .refine((obj) => Object.keys(obj).length > 0, 'At least one field must be provided');
/**
 * Tag entry. Private tags (PRD §5.3) are granted to specific accounts via
 * the `tag_owners` m2m table and are physically invisible to other tenants
 * via RLS (migration 0008 policy `tags_read`).
 */
export const tagSchema = z.object({
    id: uuidSchema,
    category_id: uuidSchema,
    name: z.string().min(1).max(255),
    /**
     * Machine key (venue-category-discovery arch §2.2, migration 0116). `null`
     * for every tag that predates the feature — only the three `day_part` tags
     * carry one today.
     *
     * IT EXISTS BECAUSE A NAME IS NOT A KEY. D-11 requires the three day-part
     * tags to correspond one-to-one with `schedule_entries.part`, and binding
     * that correspondence to the tag NAME is the exact failure ADR-031 documents
     * for `energy_range`: renaming a seeded tag silently breaks the mapping.
     */
    code: z.string().min(1).max(64).nullable().optional(),
    /**
     * Tenant-facing label (D-12). Resolved SERVER-side as
     * `coalesce(client_name, name)` and put on player wires as a single `label`
     * — the client never falls back, same doctrine as ADR-030's `play_window`.
     * `null` means «no Russian label yet», which is a measured state
     * (`tags_client_name_missing`), not a defect.
     *
     * Tag `name` is never rewritten by this feature: it is embedded in
     * `filter_rules` of every published preset version (immutable, migration
     * 0068) and in catalog filters.
     */
    client_name: z.string().min(1).max(255).nullable().optional(),
    is_private: z.boolean(),
    /**
     * Свой порядок внутри категории; `null` — не задан (идёт после заданных).
     *
     * Необязательное, как `code` и `client_name` рядом: поле аддитивное, и
     * проекция, собранная до его появления, обязана оставаться валидной — иначе
     * протухший бандл роняет разбор на `.strict()`-схемах.
     */
    sort_order: z.number().int().nullable().optional(),
    created_at: isoDateTimeSchema,
    deleted_at: isoDateTimeSchema.nullable(),
});
export const tagCreateSchema = z
    .object({
    category_id: uuidSchema,
    name: z.string().min(1).max(255),
    /**
     * Machine key — lowercase snake_case, unique per category (partial unique
     * index `tags_category_code_unique`, migration 0116). Optional here and
     * REQUIRED by the service for a tag in a `scope='preset'` system category
     * (arch §2.2): that rule needs the category row, which zod cannot see.
     */
    code: z
        .string()
        .trim()
        .min(1)
        .max(64)
        .regex(/^[a-z][a-z0-9_]*$/, 'Lowercase snake_case')
        .optional(),
    client_name: z.string().trim().min(1).max(255).nullable().optional(),
    is_private: z.boolean().default(false),
    /** Свой порядок внутри категории; опустить — «не задан» (идёт после заданных). */
    sort_order: z.number().int().nullable().optional(),
    owner_account_ids: z.array(uuidSchema).max(100).optional(),
})
    .strict();
/**
 * `PATCH /admin/tags/:id` (D-12, arch §3.3).
 *
 * `code` is deliberately NOT patchable. It is the machine key the schedule
 * model reads (`day_part` ↔ `schedule_entries.part`); a renameable machine key
 * is the `energy_range` failure ADR-031 documents, one indirection later.
 * Correcting one is a migration, not an admin edit.
 */
export const tagUpdateSchema = z
    .object({
    name: z.string().min(1).max(255).optional(),
    client_name: z.string().trim().min(1).max(255).nullable().optional(),
    is_private: z.boolean().optional(),
    /**
     * Move the tag to another category (CEO 2026-09-01, for the dictionary's
     * bulk actions). The category decides where a tag may be used — `scope` —
     * so this is how a tag written into the wrong one gets fixed instead of
     * being deleted and retyped.
     */
    category_id: uuidSchema.optional(),
    /** `null` снимает свой порядок и возвращает тег в алфавитный хвост. */
    sort_order: z.number().int().nullable().optional(),
})
    .strict()
    .refine((obj) => Object.keys(obj).length > 0, 'At least one field must be provided');
/**
 * Tag list item returned by `GET /admin/tags` with per-tag aggregate counts
 * (admin-arch §3.5 — list returns "словарь тегов с агрегатами"). Aggregates
 * are optional because the BE may serve a lightweight projection that omits
 * them; the dictionary UI renders an em-dash placeholder when absent.
 */
export const tagListItemSchema = tagSchema.extend({
    tracks_count: z.number().int().nonnegative().optional(),
    presets_count: z.number().int().nonnegative().optional(),
    owner_account_ids: z.array(uuidSchema).optional(),
});
/**
 * Tag merge preview response (admin-arch §3.5.1) — computed counts of
 * tracks / presets / saved-filters that would be touched by a merge plus a
 * sample of impacted entities. Used by the merge confirmation dialog.
 */
export const tagMergePreviewSchema = z.object({
    source: z.object({
        id: uuidSchema,
        name: z.string(),
        category_id: uuidSchema,
        category_name: z.string(),
    }),
    target: z.object({
        id: uuidSchema,
        name: z.string(),
        category_id: uuidSchema,
        category_name: z.string(),
    }),
    category_match: z.boolean(),
    affected: z.object({
        tracks: z.number().int().nonnegative(),
        presets: z.number().int().nonnegative(),
        client_custom_presets: z.number().int().nonnegative().optional(),
        saved_filters: z.number().int().nonnegative().optional(),
        tracks_losing_only_genre_tag: z.number().int().nonnegative().optional(),
    }),
    sample_tracks: z
        .array(z.object({
        id: uuidSchema,
        title: z.string(),
        artist: z.string().nullable().optional(),
    }))
        .optional(),
    sample_presets: z
        .array(z.object({ id: uuidSchema, name: z.string() }))
        .optional(),
});
/**
 * Body for `POST /admin/tags/:id/owners` — grants a private tag to one or
 * more accounts (private tag visibility is enforced via RLS — see migration
 * 0008 `tags_read`).
 */
export const tagOwnersAddSchema = z
    .object({
    account_ids: z.array(uuidSchema).min(1).max(100),
})
    .strict();
/**
 * Sort axes for `GET /admin/tags`. `tracks_count` ranks the dictionary by how
 * many tracks carry each tag, which is what the admin needs to spot
 * under-tagged and over-tagged areas. `name` sorts case-insensitively.
 */
export const tagSortSchema = z.enum(['created_at', 'name', 'tracks_count']);
/**
 * Query filter for `GET /admin/tags` — keyset cursor pagination (cstack:
 * `(<sort>, id)`, defaulting to `created_at` DESC), category filter, name
 * prefix search, include-deleted toggle (admin-arch §3.0.1 + §3.5). Mirrors
 * the account filter shape so the FE list-page hook is reusable.
 */
export const tagListFilterSchema = z.object({
    category_id: uuidSchema.optional(),
    /**
     * DEFAULTS TO `track`, and that default is the point.
     *
     * Every surface that tags a TRACK — the track card picker, the catalog
     * facets, the bulk tag dialogs, the preset FILTER editor (`filter_rules`
     * selects track tags) — reads this endpoint with no scope of its own. After
     * migration 0116 introduced the preset-only `day_part` category, an unfiltered
     * list offered a curator «Утро» in the track picker and wrote it into
     * `content.track_tags` with no complaint: not a missing feature, but data
     * damage done by hand through a control we ourselves rendered.
     *
     * The tag DICTIONARY is the one caller that wants the whole picture and asks
     * for it explicitly with `usable_for=any`.
     */
    usable_for: tagUsableForSchema.default('track'),
    q: z.string().min(1).max(255).optional(),
    include_deleted: z.coerce.boolean().optional().default(false),
    limit: z.coerce.number().int().positive().max(200).default(50),
    cursor: z.string().max(1024).optional(),
    sort: tagSortSchema.default('created_at'),
    dir: z.enum(['asc', 'desc']).default('desc'),
});
/**
 * Query filter for `GET /admin/tag-categories` — keyset on `(sort_order, id)`
 * with optional include-hidden toggle. The dictionary UI fetches the full
 * list (max ~30 categories), but we expose pagination for consistency.
 */
export const tagCategoryListFilterSchema = z.object({
    include_hidden: z.coerce.boolean().optional().default(true),
    /**
     * Defaults to `any` — unlike the tag list. A category the picker does not use
     * simply produces no group (the FE groups TAGS and joins the category for a
     * label), so an extra row here is inert, while the dictionary and the create
     * dialog both need every category. The parameter exists for callers that want
     * to narrow deliberately.
     */
    usable_for: tagUsableForSchema.default('any'),
    limit: z.coerce.number().int().positive().max(200).default(50),
    cursor: z.string().max(1024).optional(),
});
/**
 * Paginated tag list response — admin-arch §3.0.1 envelope (items +
 * next_cursor) plus per-item aggregates from `tagListItemSchema`.
 */
export const tagListResponseSchema = z.object({
    items: z.array(tagListItemSchema),
    next_cursor: z.string().nullable(),
    total: z.number().int().nonnegative().nullable().optional(),
});
export const tagCategoryListResponseSchema = z.object({
    items: z.array(tagCategorySchema),
    next_cursor: z.string().nullable(),
    total: z.number().int().nonnegative().nullable().optional(),
});
/**
 * Tag card response — admin-arch §3.5 `GET /admin/tags/:id`. Returns the tag
 * itself plus aggregates (tracks/presets counts), category snapshot, and
 * the list of account ids that "own" the tag for private-visibility RLS.
 */
export const tagCardResponseSchema = z.object({
    tag: tagSchema,
    category: tagCategorySchema,
    aggregates: z.object({
        tracks_count: z.number().int().nonnegative(),
        presets_count: z.number().int().nonnegative(),
    }),
    owner_account_ids: z.array(uuidSchema),
});
/**
 * Body for `POST /admin/tags/merge/preview` — admin-arch §3.5.1. The
 * commit endpoint takes the same pair via URL params instead of a body.
 */
export const tagMergePreviewRequestSchema = z
    .object({
    source_id: uuidSchema,
    target_id: uuidSchema,
})
    .strict();
//# sourceMappingURL=tag.js.map